import React from "react"
import { Link } from "react-router-dom"
import { Clock, FileText } from "lucide-react"
import { studentPaperAssessorHref } from "../../../../component/student/config/studentNavItems"
import { paperDisplayTitle } from "../../PracticePapers/types"
import type { PaperAssessmentRecord } from "../types"
import {
  formatAssessorDate,
  formatAssessorTime,
  pctOf,
  scoreToneClass,
} from "../utils/format"
import ScoreRing from "./ScoreRing"
import FeedbackPhaseChip from "./FeedbackPhaseChip"
import { feedbackErrorOf, feedbackPhaseOf } from "../utils/feedbackPhase"
import "./AssessmentCard.style.scss"

interface PaperAssessmentCardProps {
  record: PaperAssessmentRecord
}

const PaperAssessmentCard = ({ record }: PaperAssessmentCardProps) => {
  const { session } = record
  const phase = feedbackPhaseOf(record)
  const ready = phase === "ready"
  const score = session.questions.reduce((a, q) => a + (q.score ?? 0), 0)
  const max = session.questions.reduce((a, q) => a + q.maxScore, 0)
  const pct = pctOf(score, max)
  const answered = session.questions.filter((q) => q.status !== "unanswered")
    .length
  const displayTitle = paperDisplayTitle(session)
  const showSubject =
    Boolean(session.subjectTitle?.trim()) &&
    displayTitle !== session.subjectTitle.trim()

  return (
    <article className="assessment-card">
      <header className="assessment-card__header">
        <div className="assessment-card__chips">
          <span className="assessment-card__chip is-paper">
            <FileText size={12} /> Practice Paper
          </span>
          <span className="assessment-card__chip">{displayTitle}</span>
          {showSubject && (
            <span className="assessment-card__chip">
              {session.subjectTitle}
            </span>
          )}
          <FeedbackPhaseChip phase={phase} />
        </div>
        <span className="assessment-card__date">
          {formatAssessorDate(record.date)}
        </span>
      </header>

      <div className="assessment-card__body">
        {!ready ? (
          <>
            <p className="assessment-card__title">
              {answered}/{session.questions.length} answered
            </p>
            <p className="assessment-card__notice">
              {phase === "pending"
                ? "AI is marking this paper. Feedback will show here when it’s ready."
                : feedbackErrorOf(record) ||
                  "AI marking could not be completed for this paper."}
            </p>
          </>
        ) : (
          <>
            <div className="assessment-card__summary">
              <ScoreRing pct={pct} />
              <div className="assessment-card__copy">
                <p className="assessment-card__title">
                  {score}/{max} marks total
                </p>
                <div className="assessment-card__meta">
                  <span>
                    <Clock size={12} />{" "}
                    {formatAssessorTime(session.totalTimeSpentSeconds)}
                  </span>
                  <span>·</span>
                  <span>
                    {answered}/{session.questions.length} answered
                  </span>
                </div>
              </div>
            </div>

            <div className="assessment-card__qchips">
              {session.questions.map((q, idx) => {
                const qPct =
                  q.score !== null ? pctOf(q.score, q.maxScore) : null
                return (
                  <span
                    key={`${q.questionId}-${idx}`}
                    className={`assessment-card__qchip ${
                      qPct == null ? "is-empty" : scoreToneClass(qPct)
                    }`}
                  >
                    Q{idx + 1}:{" "}
                    {q.score !== null ? `${q.score}/${q.maxScore}` : "—"}
                  </span>
                )
              })}
            </div>

            <div className="assessment-card__bar">
              <div
                className={`assessment-card__bar-fill ${scoreToneClass(pct)}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </>
        )}
      </div>

      {ready && (
        <Link
          className="assessment-card__toggle"
          to={studentPaperAssessorHref(session.id)}
        >
          View feedback
        </Link>
      )}
    </article>
  )
}

export default PaperAssessmentCard
